/**
 * VOICE mode (spec §1, §2).
 *
 * The two-panel live reading surface: original on the left, translation on the
 * right, both updating in place as the recogniser produces partials. The
 * settled transcript accumulates below.
 *
 * The panel that is "live" gets a subtle treatment, because during speech only
 * one of the two is actually changing and the eye should know which.
 */

import type { LanguageCode } from "@shared/types/language";
import type { TranslationSession } from "../lib/use-session";
import type { PiperGender } from "../lib/piper";
import { Badge, Button, Empty, Panel } from "./ui";
import { BrowserAsrNotice } from "./MicButton";
import { MicDock } from "./MicDock";
import { TranscriptList } from "./TranscriptList";
import { LanguageBar } from "./LanguageBar";
import { PiperVoiceControl, sampleLine } from "./PiperVoiceControl";
import { IconGlobe, IconMic, IconShield, IconSpeaker } from "./icons";

interface Props {
  session: TranslationSession;
  sourceLanguage: LanguageCode | "auto";
  targetLanguage: LanguageCode;
  onSourceChange: (value: LanguageCode | "auto") => void;
  onTargetChange: (value: LanguageCode) => void;
  onSwap: () => void;
  showOriginal: boolean;
  showTranslation: boolean;
  canSpeak: (language: LanguageCode) => boolean;
  speakOut: boolean;
  onSpeakOutChange: (value: boolean) => void;
  piperGender: PiperGender;
  onPiperGenderChange: (gender: PiperGender) => void;
  onToast: (title: string, tone: "info" | "success" | "warn" | "error", detail?: string) => void;
}

export function VoiceMode(props: Props) {
  const { session } = props;
  const partial = session.partial;
  const lastEntry = session.transcript.entries[session.transcript.entries.length - 1];

  return (
    <div className="stage split">
      <div className="split__main">
        <LanguageBar
          source={props.sourceLanguage}
          target={props.targetLanguage}
          onSourceChange={props.onSourceChange}
          onTargetChange={props.onTargetChange}
          onSwap={props.onSwap}
          disabled={session.isLive}
          detectedLabel={session.detection?.label ?? null}
        />

        <div
          className="row-between wrap"
          style={{
            padding: "var(--space-2xs) var(--space-s)",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-m)",
            background: "var(--bg-panel)",
          }}
        >
          <PiperVoiceControl
            targetLanguage={props.targetLanguage}
            gender={props.piperGender}
            onGenderChange={props.onPiperGenderChange}
            speakOut={props.speakOut}
            onSpeakOutChange={props.onSpeakOutChange}
            piper={session.piper}
            piperVoice={session.piperVoice}
            onEnsureVoice={() => void session.ensurePiperVoice()}
            onTestVoice={() => session.testVoice(props.targetLanguage, sampleLine(props.targetLanguage))}
            onStopSpeech={session.cancelSpeech}
            speaking={session.speaking}
          />
          {lastEntry && (lastEntry.translatedText || lastEntry.sourceText) ? (
            <Button
              size="sm"
              onClick={() => session.speak(lastEntry.translatedText || lastEntry.sourceText, lastEntry.targetLanguage)}
              disabled={!props.canSpeak(props.targetLanguage)}
              title="Replay the last translation"
              aria-label="Replay the last translation"
            >
              <IconSpeaker size={14} /> <span className="hide-sm">Replay</span>
            </Button>
          ) : null}
        </div>

        {session.unsupportedReason ? (
          <div className="badge badge--warn" style={{ height: "auto", padding: "var(--space-2xs) var(--space-s)", lineHeight: 1.5 }}>
            <IconShield size={13} /> {session.unsupportedReason}
          </div>
        ) : null}

        {!session.isSupported ? (
          <div className="badge badge--warn" style={{ height: "auto", padding: "var(--space-2xs) var(--space-s)", lineHeight: 1.5 }}>
            <IconMic size={13} /> This browser cannot capture microphone audio. Use a Chromium- or Safari-based browser, or switch to Text mode.
          </div>
        ) : null}

        <div className="stack" style={{ gap: "var(--space-s)" }}>
          {props.showOriginal ? (
            <StreamPanel
              title="Original speech"
              language={session.detection?.code ?? (props.sourceLanguage === "auto" ? null : props.sourceLanguage)}
              live={session.isLive}
              text={partial?.source ?? ""}
              placeholder="Press the microphone and start speaking."
              empty={lastEntry?.sourceText ?? ""}
              variant="source"
            />
          ) : null}

          {props.showTranslation ? (
            <StreamPanel
              title="Translation"
              language={props.targetLanguage}
              live={session.isLive}
              text={partial?.target ?? ""}
              placeholder="The translation appears here as you speak."
              empty={lastEntry?.translatedText ?? ""}
              variant="target"
            />
          ) : null}
        </div>

        <BrowserAsrNotice active={session.usingBrowserAsr && session.browserAsrActive} />

        <MicDock
          live={session.isLive}
          busy={session.isStarting}
          level={session.level}
          onToggle={() => void (session.isLive ? session.stop() : session.start())}
          toggleLabel={session.isLive ? "Stop translation" : "Start translation"}
          disabled={!session.isSupported}
          onClear={() => void session.reset()}
          clearDisabled={session.transcript.entries.length === 0 && !partial}
          clearLabel="Clear the transcript and context"
        />
      </div>

      <div className="split__aside">
        <TranscriptList
          transcript={session.transcript}
          onClear={session.clearTranscript}
          onSpeak={(entry) => session.speak(entry.translatedText || entry.sourceText, entry.targetLanguage)}
          canSpeak={props.canSpeak(props.targetLanguage)}
          emptyHint="Utterances are added here as they finish. Export to TXT, JSON, SRT or VTT at any time."
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function StreamPanel({
  title,
  language,
  live,
  text,
  placeholder,
  empty,
  variant,
}: {
  title: string;
  language: string | null;
  live: boolean;
  text: string;
  placeholder: string;
  empty: string;
  variant: "source" | "target";
}) {
  const hasPartial = text.trim() !== "";
  const body = hasPartial ? text : empty;

  return (
    <Panel
      className="stream"
      title={
        <>
          {title}
          {live ? <Badge tone={variant === "target" ? "good" : "accent"}>{variant === "target" ? "live" : "listening"}</Badge> : null}
        </>
      }
      actions={language ? <Badge>{language}</Badge> : <IconGlobe size={14} className="dim" />}
    >
      <div className="stream__body">
        {body.trim() === "" ? (
          <Empty title={placeholder} />
        ) : (
          <p
            className={`stream__text stream__text--${variant}${hasPartial ? " is-partial" : ""}`}
            dir="auto"
            lang={language ?? undefined}
            aria-live={variant === "target" ? "polite" : "off"}
          >
            {body}
            {hasPartial && live ? <span className="stream__cursor" /> : null}
          </p>
        )}
      </div>
    </Panel>
  );
}
